import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSignalR } from "src/hooks";
import { configuration } from "src/configuration";
import { ActionItemDetailDto, ActionItemSummaryDto, FetchError, queryKeys, useAccessToken } from "src/http";

type Variables = {
  actionItemGuid: string;
  planGuid: string;
};

const useDeleteActionItemRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function deleteActionItemRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const response = await fetch(`${configuration.apiRootUrl}/action-items/${variables.actionItemGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "DELETE",
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { deleteActionItemRequest };
};

const useDeleteActionItem = () => {
  const { deleteActionItemRequest } = useDeleteActionItemRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: deleteActionItemRequest,
    onMutate: async (variables) => {
      await queryClient.cancelQueries(queryKeys.actionItems.detail(variables.actionItemGuid));
      await queryClient.cancelQueries(queryKeys.actionItems.summary(variables.actionItemGuid));

      const cachedDetailData = queryClient.getQueryData<ActionItemDetailDto>(
        queryKeys.actionItems.detail(variables.actionItemGuid),
      );
      queryClient.removeQueries(queryKeys.actionItems.detail(variables.actionItemGuid));
      const cachedSummaryData = queryClient.getQueriesData<ActionItemSummaryDto>(
        queryKeys.actionItems.summary(variables.actionItemGuid),
      );

      return { cachedSummaryData, cachedDetailData };
    },
    onError: (_error, variables, context) => {
      queryClient.setQueriesData(queryKeys.actionItems.summary(variables.actionItemGuid), context?.cachedSummaryData);
      queryClient.setQueryData(queryKeys.actionItems.detail(variables.actionItemGuid), context?.cachedDetailData);
    },
    onSuccess: (_data, variables, _context) => {
      signalR.sendRefetchPlan(variables.planGuid);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));
    },
  });
};

export { useDeleteActionItem };
